import * as React from 'react';

import {
  enumScaleConnectionType,
  enumScaleMeasurementMode,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DialogClosingConfirmation,
  EntityDetails,
  ThemeColor,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { scaleDeleteAtom } from '../../../_graphql/mutationScaleDelete';
import { scaleUpdateAtom } from '../../../_graphql/mutationScaleUpdate';
import { useScaleEventsSubscription } from '../../../_graphql/subscriptionScaleEvents';
import * as state from '../../../_state/scaleDetail.state';
import {
  $discardScaleBaseline,
  $hasUnsavedScaleChanges,
  $resetScaleFormToScale,
  ScaleFormState,
} from '../../../_state/scaleForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { usePopulateScaleForm } from '../../../hooks/usePopulateScaleForm';
import { useTranslations } from '../../../i18n/useTranslations';

import { ScaleTabInfo } from './ScaleTabInfo';
import { ScaleTabWeight } from './ScaleTabWeight';

import { useHardwareSidecarConnection } from '~/shared/providers/libp2p/useHardwareSidecarConnection';
import { useHardwarePresenceMembers } from '~/shared/providers/presence';

const STATUS_COLORS: Record<string, ThemeColor> = {
  Discovered: 'neutral',
  Configured: 'warning',
  Active: 'success',
};

type ScaleDetailTab = 'info' | 'weight';

export const ScaleDetailView = () => {
  const t = useTranslations();
  const toast = useToast();
  const [, setLocation] = useSettingsLocation();
  const [currentTab, setCurrentTab] = React.useState<ScaleDetailTab>('info');
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<ScaleDetailTab | null>(null);

  const hasUnsavedChanges = useAtomValue($hasUnsavedScaleChanges);
  const [, resetForm] = useAtom($resetScaleFormToScale);
  const [discardBaseline, setDiscardBaseline] = useAtom($discardScaleBaseline);

  const deleteScale = useSetAtom(scaleDeleteAtom({}));
  const updateScale = useSetAtom(scaleUpdateAtom({}));

  const scaleId = useAtomValue(state.$scaleId);
  const sidecarId = useAtomValue(state.$scaleSidecarId);
  const { data: scale, refetch } = useAtomValue(state.$remoteScaleDetail);
  const isLoading = useAtomValue(state.$isScaleLoading);
  const error = useAtomValue(state.$scaleError);
  const presenceMembers = useHardwarePresenceMembers();
  const { connection, isConnecting } = useHardwareSidecarConnection({
    sidecar: scale?.sidecarDetail,
  });

  usePopulateScaleForm({ scale });

  useScaleEventsSubscription(undefined, {
    onEvent: (event) => {
      if (event?.id === scaleId) {
        refetch?.();
      }
    },
  });

  const handleBack = React.useCallback(() => {
    setLocation('/hardware');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.hardwareView.scaleDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: ScaleFormState) => {
      if (!scaleId || !data) return;

      const isTCP = data.connectionType === enumScaleConnectionType.TCP;

      try {
        setIsUpdating(true);
        await updateScale({
          id: scaleId,
          name: data.name.trim(),
          connectionType: data.connectionType,
          scaleFormatType: data.scaleFormatType as Parameters<
            typeof updateScale
          >[0]['scaleFormatType'],
          measurementMode: data.measurementMode,
          onDemandString:
            data.measurementMode === enumScaleMeasurementMode.OnDemand
              ? data.onDemandString || null
              : null,
          tcpHost: isTCP ? data.tcpHost || null : null,
          tcpPort: isTCP ? parseInt(data.tcpPort, 10) || null : null,
          serialPortPath: !isTCP ? data.serialPortPath || null : null,
          baudRate: !isTCP ? parseInt(data.baudRate, 10) || null : null,
          dataBits: !isTCP ? parseInt(data.dataBits, 10) || null : null,
          stopBits: !isTCP ? data.stopBits || null : null,
          parity: !isTCP ? data.parity || null : null,
          unitOfMeasurement: data.unitOfMeasurement || null,
          cameraId: data.cameraId,
        });

        setDiscardBaseline(data);
        toast.success(t.hardwareView.scaleDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.hardwareView.scaleDetailView.toast.updateError(
            err instanceof Error
              ? err.message
              : t.hardwareView.common.unknownError,
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [scaleId, updateScale, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (currentTab === 'info' && hasUnsavedChanges) {
        setPendingTabChange(newTab as ScaleDetailTab);
        return false;
      }
      return true;
    },
    [currentTab, hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!scaleId) return;

    try {
      await deleteScale({ id: scaleId });
      toast.success(t.hardwareView.scaleDetailView.messages.deleteSuccess);
      setLocation('/hardware');
    } catch (err) {
      toast.error(
        t.hardwareView.scaleDetailView.messages.deleteError(
          err instanceof Error
            ? err.message
            : t.hardwareView.common.unknownError,
        ),
      );
      throw err;
    }
  }, [scaleId, deleteScale, toast, setLocation, t]);

  if (isLoading && !scale) {
    return (
      <div className="@ScaleDetailView p-6">
        <div className="h-32 animate-pulse rounded bg-neutral/20" />
      </div>
    );
  }

  if (error || !scale || !scaleId || !sidecarId) {
    return (
      <div className="@ScaleDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          {t.hardwareView.common.errorLabel}:{' '}
          {error?.message ??
            t.hardwareView.scaleDetailView.messages.scaleNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.hardwareView.scaleDetailView.actions.backToHardware}
        </Button>
      </div>
    );
  }

  const isOnline = presenceMembers.has(scale.id);

  return (
    <div className="@ScaleDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as ScaleDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={scale.name}
        headerActions={
          <ConfirmPopoverButton
            onConfirm={handleDelete}
            message={t.hardwareView.scaleDetailView.messages.deleteConfirmation}
            confirmLabel={t.hardwareView.common.confirmDelete}
            cancelLabel={t.hardwareView.common.cancel}
            buttonProps={{
              variant: 'outline',
              color: 'failure',
              icon: <Trash2 className="size-4" />,
            }}
          >
            {t.hardwareView.scaleDetailView.actions.delete}
          </ConfirmPopoverButton>
        }
        subtitle={
          <div className="flex items-center gap-2">
            <Badge color={STATUS_COLORS[scale.status] ?? 'warning'}>
              {t.hardwareView.scaleDetailView.statusLabels[
                scale.status as keyof typeof t.hardwareView.scaleDetailView.statusLabels
              ] ?? scale.status}
            </Badge>
            <Badge color={isOnline ? 'success' : 'neutral'}>
              {isOnline
                ? t.hardwareView.scaleDetailView.presenceLabels.online
                : t.hardwareView.scaleDetailView.presenceLabels.offline}
            </Badge>
            {isConnecting && (
              <Badge color="warning">
                {t.hardwareView.scaleDetailView.connectionLabels.connecting}
              </Badge>
            )}
            {connection?.connected && (
              <Badge
                color={connection.type === 'direct' ? 'primary' : 'neutral'}
              >
                {connection.type === 'direct'
                  ? t.hardwareView.scaleDetailView.connectionLabels.direct
                  : t.hardwareView.scaleDetailView.connectionLabels.relayed}
              </Badge>
            )}
            {!connection?.connected && !isConnecting && (
              <Badge color="warning">
                {t.hardwareView.scaleDetailView.connectionLabels.disconnected}
              </Badge>
            )}
          </div>
        }
        tabs={{
          info: {
            title: t.hardwareView.scaleDetailView.tabs.info,
            content: () => (
              <ScaleTabInfo
                scale={scale}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
          weight: {
            title: t.hardwareView.scaleDetailView.tabs.weight,
            content: () => (
              <ScaleTabWeight
                scaleId={scaleId}
                sidecarId={sidecarId}
                sidecarDetail={scale.sidecarDetail}
              />
            ),
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.hardwareView.scaleDetailView.messages.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
